import { createArcadeAudio } from './84-bob-audio.mjs';
import { paintIconAction } from './82-icon-actions.mjs';
import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import { uiText } from '../04-i18n.mjs';

/** Private opt-in movement/reward cues from the existing CC0 sample library.
 * Accepted canonical state supplies every reward; pointer movement adds one soft
 * lift, never a per-frame sound. No storage, audio on load or delayed event queue. */
export function createGameSound(
  ctx,
  {
    controls,
    more,
    action,
    board,
    feedback,
    enabled = () => true,
    audioFactory = createArcadeAudio,
  },
) {
  let disposed = false,
    wanted = false,
    busy = false,
    version = 0,
    pointer = null;
  const statuses = {
    'Sound on': 'Game sounds on',
    'Sound off': 'Game sounds off',
    'Starting sound…': 'Starting game sounds…',
    'Sound unavailable · tap to retry': 'Game sound unavailable · Tap to retry',
  };
  const audio = audioFactory({
    onStatus(status) {
      if (disposed) return;
      if (status.startsWith('Sound unavailable')) wanted = false;
      if (feedback) feedback.textContent = uiText(statuses[status] ?? status);
      paint();
    },
  });
  const makeAction =
    action ??
    ((_text, _label, click) => {
      const node = ctx.e('button', 'game-action');
      node.type = 'button';
      node.addEventListener('click', click);
      return node;
    });
  const button = makeAction('', 'Turn game sounds on', toggle, 'volume-x');
  if (controls.insertBefore) controls.insertBefore(button, more ?? null);
  else controls.append(button);
  function paint() {
    paintIconAction(
      ctx,
      button,
      wanted ? 'volume-2' : 'volume-x',
      wanted ? 'Turn game sounds off' : 'Turn game sounds on',
    );
    button.setAttribute('aria-pressed', String(wanted));
    button.setAttribute('aria-busy', String(busy));
  }
  async function toggle() {
    if (disposed) return;
    const token = ++version;
    wanted = !wanted;
    busy = wanted;
    paint();
    const ready = await audio.enable(wanted);
    if (disposed || token !== version) return;
    busy = false;
    wanted = ready;
    paint();
    if (ready) cue('shield', { volume: 0.35 });
  }
  const events = [];
  const listen = (target, name, handler, options) => {
    target?.addEventListener?.(name, handler, options);
    events.push([target, name, handler, options]);
  };
  const stop = () => {
    pointer = null;
    audio.stop();
  };
  listen(board, 'pointerdown', (event) => {
    if (
      !wanted ||
      !enabled() ||
      event.isPrimary === false ||
      event.button > 0 ||
      pointer ||
      (event.pointerType === 'touch' && board.dataset?.immersive !== 'true')
    )
      return;
    audio.resume();
    pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, lifted: false };
  });
  listen(board, 'pointermove', (event) => {
    if (pointer?.id !== event.pointerId || pointer.lifted || !enabled()) return;
    if (Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) < 8) return;
    pointer.lifted = true;
    audio.cue('shield', { volume: 0.22, channel: 'movement', cooldown: 0.12 });
  });
  listen(board, 'pointerup', (event) => {
    if (pointer?.id === event.pointerId) pointer = null;
  });
  for (const name of ['pointercancel', 'lostpointercapture'])
    listen(board, name, (event) => {
      if (pointer?.id === event.pointerId) stop();
    });
  const doc = board.ownerDocument ?? globalThis.document;
  listen(doc, 'visibilitychange', () => {
    if (doc.hidden) {
      pointer = null;
      audio.suspend();
    }
  });
  listen(doc?.defaultView ?? globalThis.window, 'blur', stop);
  paint();
  function cue(name, options) {
    if (wanted && !disposed && enabled() && !doc?.hidden) audio.cue(name, options);
  }
  return {
    stop,
    cue,
    progress(previous, value) {
      if (!value) return;
      if (value.completed && !previous?.completed)
        cue('finish', { volume: 0.65, channel: 'reward' });
      else if (previous && Number.isFinite(value.stage) && value.stage > previous.stage)
        cue('checkpoint', { volume: 0.6, channel: 'reward' });
      else if (previous && Number.isFinite(value.score) && value.score > previous.score)
        cue('pickup', { volume: 0.5 });
    },
    accepted(previous, state) {
      if (disposed || !wanted || !enabled() || state === previous || doc?.hidden) return;
      const view = gameView(state),
        events = view.events ?? [];
      const earned = state.score - previous.score;
      if (state.completed || view.stageComplete)
        audio.cue('finish', { volume: 0.65, channel: 'reward' });
      else if (
        events.some(
          (event) => event.combination || event.earned?.length || event.type === 'milestone',
        )
      )
        audio.cue('checkpoint', { volume: 0.6, channel: 'reward' });
      else if (earned > 0)
        audio.cue('pickup', {
          volume: 0.55,
          rate: 1 + Math.min(5, view.chain || view.combo || 0) * 0.05,
        });
      else if (events.some((event) => ['invalid', 'miss'].includes(event.type)))
        audio.cue('stumble', { volume: 0.2 });
      else if (state.moves > previous.moves) audio.cue('bounce', { volume: 0.2, rate: 1.15 });
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      version++;
      pointer = null;
      for (const [target, name, handler, options] of events)
        target?.removeEventListener?.(name, handler, options);
      audio.dispose();
      button.remove();
    },
  };
}
